import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Monitor, Smartphone, Tablet, Globe, Plus, TrendingUp, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const DEVICE_ICONS = { DESKTOP: Monitor, MOBILE: Smartphone, TABLET: Tablet };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function GSCKeywordsTable({ keywords, sortField, sortDir, onToggleSort, clientId, clientName }) {
  const [addingKeyword, setAddingKeyword] = useState('');
  const [addedKeywords, setAddedKeywords] = useState(new Set());

  const getHeaderLabel = (f) => {
    const labels = { clicks: 'Clics', impressions: 'Impr.', ctr: 'CTR', position: 'Pos.' };
    return labels[f] || f;
  };

  const handleAddToTracking = async (keyword) => {
    if (!clientId) {
      toast.error('Selectionnez un client');
      return;
    }
    setAddingKeyword(keyword);
    try {
      await axios.post(`${API}/keywords`, {
        keyword,
        client_id: clientId,
        priority: 'medium',
        location: 'France',
        language: 'French',
        target_url: '',
      }, { withCredentials: true });
      toast.success(`"${keyword}" ajoute au suivi de ${clientName}`);
      setAddedKeywords(prev => new Set(prev).add(keyword));
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || '';
      if (status === 409 || detail.includes('existe') || detail.includes('duplicate')) {
        toast.info(`"${keyword}" est deja dans le suivi`);
        setAddedKeywords(prev => new Set(prev).add(keyword));
      } else {
        toast.error(detail || `Erreur ajout "${keyword}"`);
      }
    } finally {
      setAddingKeyword('');
    }
  };

  return (
    <div className="lg:col-span-2 bg-white border border-neutral-200 p-5">
      <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-4">
        Mots-cles ({keywords.length})
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-neutral-500">
              <th className="pb-2 pr-4">Mot-cle</th>
              {['clicks', 'impressions', 'ctr', 'position'].map(f => (
                <th key={f} className="pb-2 px-2 text-right cursor-pointer select-none" onClick={() => onToggleSort(f)}>
                  <span className="inline-flex items-center gap-1">
                    {getHeaderLabel(f)}
                    {sortField === f && (sortDir === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)}
                  </span>
                </th>
              ))}
              <th className="pb-2 px-2 text-right w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {keywords.slice(0, 50).map((k) => (
              <tr key={k.keyword} className="border-b border-neutral-50 hover:bg-neutral-50">
                <td className="py-2 pr-4 text-neutral-800 max-w-[200px] truncate" title={k.keyword}>{k.keyword}</td>
                <td className="py-2 px-2 text-right font-mono font-medium text-[#002FA7]">{k.clicks}</td>
                <td className="py-2 px-2 text-right font-mono text-neutral-600">{k.impressions.toLocaleString('fr-FR')}</td>
                <td className="py-2 px-2 text-right font-mono text-[#2A9D8F]">{k.ctr}%</td>
                <td className="py-2 px-2 text-right font-mono text-neutral-700">{k.position}</td>
                <td className="py-2 px-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {addedKeywords.has(k.keyword) ? (
                      <span className="text-green-600" title="Ajoute au suivi">
                        <Check className="w-4 h-4" />
                      </span>
                    ) : (
                      <button
                        onClick={() => handleAddToTracking(k.keyword)}
                        disabled={addingKeyword === k.keyword}
                        className="p-1.5 hover:bg-[#002FA7]/10 rounded text-[#002FA7] transition-colors"
                        title="Ajouter au suivi de mots-cles"
                        data-testid={`add-keyword-${k.keyword}`}
                      >
                        {addingKeyword === k.keyword
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Plus className="w-4 h-4" />}
                      </button>
                    )}
                    <a
                      href={`/keyword-evolution?keyword=${encodeURIComponent(k.keyword)}&client_id=${clientId}`}
                      className="p-1.5 hover:bg-[#002FA7]/10 rounded text-[#002FA7] transition-colors"
                      title="Voir l'evolution"
                    >
                      <TrendingUp className="w-4 h-4" />
                    </a>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function GSCSidebar({ devices, countries }) {
  return (
    <div className="space-y-4">
      {devices.length > 0 && (
        <div className="bg-white border border-neutral-200 p-5">
          <h2 className="font-heading font-semibold text-base text-neutral-900 mb-4">Appareils</h2>
          <div className="space-y-3">
            {devices.map((d) => {
              const DeviceIcon = DEVICE_ICONS[d.device] || Globe;
              const total = devices.reduce((s, x) => s + x.clicks, 0);
              const pct = total > 0 ? ((d.clicks / total) * 100).toFixed(1) : 0;
              return (
                <div key={d.device} className="flex items-center gap-3">
                  <DeviceIcon className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-700 capitalize">{d.device.toLowerCase()}</span>
                      <span className="font-mono text-neutral-900">{pct}%</span>
                    </div>
                    <div className="w-full bg-neutral-100 rounded-full h-1.5 mt-1">
                      <div className="bg-[#002FA7] h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {countries.length > 0 && (
        <div className="bg-white border border-neutral-200 p-5">
          <h2 className="font-heading font-semibold text-base text-neutral-900 mb-4">Pays (top 10)</h2>
          <div className="space-y-2">
            {countries.slice(0, 10).map((c, idx) => (
              <div key={c.country} className="flex items-center justify-between py-1.5 border-b border-neutral-50 last:border-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neutral-400 font-mono w-4">{idx + 1}</span>
                  <span className="text-sm text-neutral-700">{c.country}</span>
                </div>
                <div className="flex items-center gap-3 text-sm font-mono">
                  <span className="text-[#002FA7] font-medium">{c.clicks}</span>
                  <span className="text-neutral-400 text-xs">{c.impressions.toLocaleString('fr-FR')} impr.</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function GSCPagesTable({ pages }) {
  if (!pages || pages.length === 0) return null;

  return (
    <div className="bg-white border border-neutral-200 p-5">
      <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-4">
        Pages les plus performantes
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-neutral-500">
              <th className="pb-2 pr-4">#</th>
              <th className="pb-2 pr-4">Page</th>
              <th className="pb-2 px-2 text-right">Clics</th>
              <th className="pb-2 px-2 text-right">Impressions</th>
              <th className="pb-2 px-2 text-right">CTR</th>
              <th className="pb-2 px-2 text-right">Position</th>
            </tr>
          </thead>
          <tbody>
            {pages.slice(0, 20).map((p, idx) => (
              <tr key={p.page} className="border-b border-neutral-50 hover:bg-neutral-50">
                <td className="py-2 pr-4 text-neutral-400 font-mono text-xs">{idx + 1}</td>
                <td className="py-2 pr-4 text-neutral-700 max-w-[400px] truncate" title={p.page}>
                  {p.page.replace(/https?:\/\/[^/]+/, '')}
                </td>
                <td className="py-2 px-2 text-right font-mono font-medium text-[#002FA7]">{p.clicks}</td>
                <td className="py-2 px-2 text-right font-mono text-neutral-600">{p.impressions.toLocaleString('fr-FR')}</td>
                <td className="py-2 px-2 text-right font-mono text-[#2A9D8F]">{p.ctr}%</td>
                <td className="py-2 px-2 text-right font-mono text-neutral-700">{p.position}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
